import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Seo from '../components/Seo';
import { ChatWidget, BackToTop } from '../components/ChatAndUtils';
import { Button } from '../components/ui/button';
import { ArrowRight, ArrowLeft, Info, RotateCcw, CheckCircle2, AlertTriangle, XCircle, ClipboardList, Clock, Euro } from 'lucide-react';
import { evaluateSelbsttest } from '../data/selbsttestData';
import { useLocalizedData } from '../i18n/dataLoader';

const STATUS_STYLES = {
  green: { bg: 'bg-emerald-50', border: 'border-emerald-500', text: 'text-emerald-700', Icon: CheckCircle2, label: 'VORAUSSETZUNGEN ERFÜLLT' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-500', text: 'text-amber-700', Icon: AlertTriangle, label: 'KLÄRUNG ERFORDERLICH' },
  red: { bg: 'bg-rose-50', border: 'border-rose-500', text: 'text-rose-700', Icon: XCircle, label: 'AKTUELL NICHT MÖGLICH' },
};

const EinbuergerungsSelbsttest = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(-1); // -1 = Welcome, 0..10 = Questions, 11 = Result
  const [answers, setAnswers] = useState({});

  const selbsttestData = useLocalizedData('selbsttest');
  const selbsttestQuestions = selbsttestData?.selbsttestQuestions || [];

  const totalQuestions = selbsttestQuestions.length;
  const progress = step >= 0 && step < totalQuestions ? ((step + 1) / totalQuestions) * 100 : 0;
  const currentQuestion = step >= 0 && step < totalQuestions ? selbsttestQuestions[step] : null;

  const handleAnswer = (qId, value) => {
    setAnswers({ ...answers, [qId]: value });
  };

  const next = () => setStep((s) => s + 1);
  const back = () => setStep((s) => Math.max(-1, s - 1));
  const restart = () => {
    setStep(-1);
    setAnswers({});
  };

  const result = step >= totalQuestions ? evaluateSelbsttest(answers) : null;

  const goToBeratung = () => {
    // Testergebnis in sessionStorage ablegen, damit die Mandantenerfassung
    // anbieten kann, es als Anhang mitzusenden.
    if (result) {
      try {
        const payload = {
          completed_at: new Date().toISOString(),
          status: result.status,
          status_label: STATUS_STYLES[result.status]?.label,
          title: result.title,
          subtitle: result.subtitle,
          basis: result.basis,
          hits: result.hits,
          blockers: result.blockers,
          actions: result.actions,
          answers,
        };
        sessionStorage.setItem('einbuergerung_selbsttest_result', JSON.stringify(payload));
      } catch (err) {
        console.warn('[selbsttest] Ergebnis konnte nicht zwischengespeichert werden', err);
      }
    }
    navigate('/mandantenerfassung?rechtsgebiet=Ausl%C3%A4nderrecht&from=selbsttest');
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="Einbürgerungs-Selbsttest – Voraussetzungen prüfen"
        description="In 11 Fragen kostenlos prüfen, ob Sie die Voraussetzungen für die deutsche Einbürgerung nach der StAG-Reform 2024/2025 erfüllen. Sofortige Auswertung."
        path="/einbuergerungs-selbsttest"
        keywords="Einbürgerung, Selbsttest, StAG, deutsche Staatsangehörigkeit, Voraussetzungen, Ausländerrecht"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'Wie lange muss ich in Deutschland leben, um eingebürgert zu werden?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Seit der StAG-Reform 2024/2025 reichen grundsätzlich 5 Jahre rechtmäßiger Aufenthalt aus. Bei besonderen Integrationsleistungen (z. B. C1-Sprachkenntnisse, ehrenamtliches Engagement, herausragende berufliche Erfolge) ist eine Einbürgerung bereits nach 3 Jahren möglich.',
                },
              },
              {
                '@type': 'Question',
                name: 'Wie lange dauert das Einbürgerungsverfahren?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Die durchschnittliche Bearbeitungsdauer bei den Behörden liegt bundesweit zwischen 12 und 24 Monaten ab vollständiger Antragstellung. Bei den von uns betreuten Mandanten erfolgt die Einbürgerung in der Regel deutlich schneller – meist zwischen 2 Monaten und 1 Jahr, durch sauber vorbereitete Unterlagen und direkte Kommunikation mit den Behörden.',
                },
              },
              {
                '@type': 'Question',
                name: 'Welche Sprachkenntnisse sind für die Einbürgerung erforderlich?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'In der Regel müssen Sie Deutschkenntnisse mindestens auf dem Niveau B1 (Gemeinsamer Europäischer Referenzrahmen) nachweisen. Für die beschleunigte Einbürgerung nach 3 Jahren ist C1 erforderlich.',
                },
              },
              {
                '@type': 'Question',
                name: 'Darf ich nach der Einbürgerung meine bisherige Staatsangehörigkeit behalten?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Seit der StAG-Reform 2024/2025 ist die Mehrstaatigkeit grundsätzlich zugelassen. Sie müssen Ihre alte Staatsangehörigkeit in der Regel nicht mehr aufgeben.',
                },
              },
              {
                '@type': 'Question',
                name: 'Muss ich für die Einbürgerung einen Einbürgerungstest bestehen?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Ja, der „Leben in Deutschland"-Test (33 Fragen, mind. 17 richtig beantworten) ist Voraussetzung. Ausgenommen sind Personen mit deutschem Schulabschluss, Hochschulabschluss in staatswissenschaftlichen Fächern oder bei nachgewiesener Unfähigkeit aus gesundheitlichen Gründen.',
                },
              },
              {
                '@type': 'Question',
                name: 'Welche Kosten fallen bei einer Einbürgerung an?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Die Gebühr für die Einbürgerung beträgt 255 € pro Person, für minderjährige Kinder, die mit eingebürgert werden, 51 €. Hinzu kommen ggf. Kosten für Sprachzertifikate, Einbürgerungstest und beglaubigte Übersetzungen.',
                },
              },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Start', item: 'https://www.kanzlei-partner.com/' },
              { '@type': 'ListItem', position: 2, name: 'Einbürgerungs-Selbsttest', item: 'https://www.kanzlei-partner.com/einbuergerungs-selbsttest' },
            ],
          },
        ]}
      />
      <Header />
      <main className="pt-[150px]">
        {/* Hero */}
        <section className="bg-[#1B3A5C] text-white py-14 lg:py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute top-10 right-10 w-96 h-96 bg-[#b8893d] rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-1/3 w-72 h-72 bg-[#3a6fa0] rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
            <div className="inline-flex items-center gap-3 text-[#e0b56a] text-xs tracking-[0.3em] mb-4">
              <span className="w-12 h-px bg-[#b8893d]"></span>
              AUSLÄNDERRECHT
              <span className="w-12 h-px bg-[#b8893d]"></span>
            </div>
            <h1 className="font-serif font-light text-4xl md:text-5xl lg:text-6xl tracking-tight mb-5">
              Einbürgerungs-Selbsttest
            </h1>
            <p className="text-white/80 text-lg leading-relaxed max-w-2xl mx-auto">
              In 11 Fragen prüfen, ob Sie die Voraussetzungen für eine deutsche Einbürgerung erfüllen.
            </p>
            <p className="text-white/50 text-xs mt-6 tracking-wide">
              Stand: Rechtslage nach StAG-Reform vom 30.10.2025
            </p>
          </div>
        </section>

        {/* Body */}
        <section className="py-12 lg:py-16 bg-[#f5f8fa] min-h-[600px]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10">
            {/* WELCOME */}
            {step === -1 && (
              <div className="bg-white p-8 lg:p-12 rounded-sm shadow-sm border-l-4 border-[#b8893d]">
                <div className="text-center">
                  <div className="w-16 h-16 bg-[#b8893d]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                    <ClipboardList className="w-8 h-8 text-[#b8893d]" />
                  </div>
                  <h2 className="font-serif text-2xl md:text-3xl text-[#1B3A5C] mb-3">
                    Können Sie sich einbürgern lassen?
                  </h2>
                  <p className="text-[#34536b] leading-relaxed max-w-xl mx-auto mb-10">
                    Beantworten Sie 11 kurze Fragen und erhalten Sie sofort eine erste Einschätzung —
                    kostenlos und unverbindlich.
                  </p>

                  <div className="grid grid-cols-3 gap-4 mb-10 max-w-md mx-auto">
                    {[
                      { icon: ClipboardList, value: '11', label: 'Fragen' },
                      { icon: Clock, value: '3', label: 'Minuten' },
                      { icon: Euro, value: '0 €', label: 'Kosten' },
                    ].map((s) => (
                      <div key={s.label} className="p-4 bg-[#f5f8fa] rounded-sm">
                        <s.icon className="w-5 h-5 text-[#b8893d] mx-auto mb-2" />
                        <div className="text-[#1B3A5C] text-2xl font-serif font-medium">{s.value}</div>
                        <div className="text-[#34536b] text-xs tracking-wider mt-1">{s.label}</div>
                      </div>
                    ))}
                  </div>

                  <Button
                    onClick={() => setStep(0)}
                    className="bg-[#b8893d] hover:bg-[#a07730] text-white px-10 py-6 tracking-[0.15em] text-xs"
                  >
                    SELBSTTEST JETZT STARTEN
                    <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>

                  <p className="text-[#34536b]/60 text-xs mt-8 max-w-xl mx-auto">
                    Dieser Selbsttest gibt eine erste Orientierung und ersetzt keine anwaltliche Beratung
                    im Einzelfall.
                  </p>
                </div>
              </div>
            )}

            {/* QUESTION */}
            {currentQuestion && (
              <div className="bg-white p-8 lg:p-10 rounded-sm shadow-sm border-l-4 border-[#b8893d]">
                {/* Progress */}
                <div className="mb-8">
                  <div className="flex justify-between text-xs text-[#34536b] mb-2 tracking-wider">
                    <span>FRAGE {step + 1} VON {totalQuestions}</span>
                    <span>{Math.round(progress)} %</span>
                  </div>
                  <div className="w-full h-1 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#b8893d] transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                <h2 className="font-serif text-2xl md:text-3xl text-[#1B3A5C] mb-3 leading-tight">
                  {currentQuestion.title}
                </h2>

                <div className="flex items-start gap-2 p-4 bg-[#f5f8fa] mb-7 rounded-sm">
                  <Info className="w-4 h-4 text-[#b8893d] flex-shrink-0 mt-0.5" />
                  <p className="text-[#34536b] text-sm leading-relaxed">{currentQuestion.help}</p>
                </div>

                <div className="space-y-3 mb-8">
                  {currentQuestion.options.map((opt) => {
                    const isSelected = answers[currentQuestion.id] === opt.v;
                    return (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => handleAnswer(currentQuestion.id, opt.v)}
                        className={`w-full flex items-center gap-4 text-left p-4 lg:p-5 border-2 rounded-sm transition-all ${
                          isSelected
                            ? 'border-[#b8893d] bg-[#fbf7f0]'
                            : 'border-gray-200 bg-white hover:border-[#b8893d]/40 hover:bg-[#fafaf7]'
                        }`}
                      >
                        <span
                          className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${
                            isSelected ? 'border-[#b8893d]' : 'border-gray-300'
                          }`}
                        >
                          {isSelected && <span className="w-2.5 h-2.5 rounded-full bg-[#b8893d]"></span>}
                        </span>
                        <span className={`text-sm leading-relaxed ${isSelected ? 'text-[#1B3A5C] font-medium' : 'text-[#34536b]'}`}>
                          {opt.l}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div className="flex justify-between items-center pt-6 border-t border-gray-100">
                  <Button
                    type="button"
                    onClick={back}
                    variant="outline"
                    className="border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white tracking-wider text-xs"
                  >
                    <ArrowLeft className="mr-2 w-3.5 h-3.5" /> ZURÜCK
                  </Button>
                  <Button
                    type="button"
                    onClick={next}
                    disabled={!answers[currentQuestion.id]}
                    className="bg-[#b8893d] hover:bg-[#a07730] text-white tracking-wider text-xs disabled:opacity-40"
                  >
                    {step === totalQuestions - 1 ? 'ERGEBNIS ANZEIGEN' : 'WEITER'}
                    <ArrowRight className="ml-2 w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* RESULT */}
            {result && (
              <div className="space-y-6">
                {/* Status card */}
                <div
                  className={`p-8 lg:p-10 rounded-sm border-l-4 ${STATUS_STYLES[result.status].bg} ${STATUS_STYLES[result.status].border}`}
                >
                  <div className="flex items-start gap-5">
                    <div className={`w-14 h-14 rounded-full bg-white flex items-center justify-center flex-shrink-0 shadow-sm ${STATUS_STYLES[result.status].text}`}>
                      {React.createElement(STATUS_STYLES[result.status].Icon, { className: 'w-7 h-7' })}
                    </div>
                    <div className="flex-1">
                      <div className={`text-xs tracking-[0.2em] font-semibold mb-2 ${STATUS_STYLES[result.status].text}`}>
                        {STATUS_STYLES[result.status].label}
                      </div>
                      <h2 className="font-serif text-2xl md:text-3xl text-[#1B3A5C] mb-2 leading-tight">
                        {result.title}
                      </h2>
                      <p className="text-[#34536b]">{result.subtitle}</p>
                      <div className="mt-4 inline-block px-3 py-1 bg-white/70 rounded text-xs text-[#1B3A5C] font-mono tracking-wide">
                        Grundlage: {result.basis}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hits */}
                {result.hits.length > 0 && (
                  <div className="bg-white p-6 lg:p-8 rounded-sm border-l-4 border-emerald-500 shadow-sm">
                    <h3 className="flex items-center gap-2 text-[#1B3A5C] font-semibold tracking-wide mb-4">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                      Das spricht für Sie
                      <span className="ml-auto bg-emerald-100 text-emerald-700 text-xs px-2 py-0.5 rounded-full">
                        {result.hits.length}
                      </span>
                    </h3>
                    <ul className="space-y-2">
                      {result.hits.map((h, i) => (
                        <li key={h} className="flex items-start gap-2 text-[#34536b] text-sm leading-relaxed">
                          <span className="text-emerald-600 mt-1">•</span>
                          <span>{h}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Blockers */}
                {result.blockers.length > 0 && (
                  <div className="bg-white p-6 lg:p-8 rounded-sm border-l-4 border-amber-500 shadow-sm">
                    <h3 className="flex items-center gap-2 text-[#1B3A5C] font-semibold tracking-wide mb-4">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      Das muss geklärt werden
                      <span className="ml-auto bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                        {result.blockers.length}
                      </span>
                    </h3>
                    <ul className="space-y-2">
                      {result.blockers.map((b, i) => (
                        <li key={b} className="flex items-start gap-2 text-[#34536b] text-sm leading-relaxed">
                          <span className="text-amber-600 mt-1">•</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                {result.actions.length > 0 && (
                  <div className="bg-white p-6 lg:p-8 rounded-sm border-l-4 border-[#b8893d] shadow-sm">
                    <h3 className="flex items-center gap-2 text-[#1B3A5C] font-semibold tracking-wide mb-4">
                      <ClipboardList className="w-5 h-5 text-[#b8893d]" />
                      Was Sie jetzt tun können
                      <span className="ml-auto bg-[#b8893d]/10 text-[#b8893d] text-xs px-2 py-0.5 rounded-full">
                        {result.actions.length}
                      </span>
                    </h3>
                    <ul className="space-y-2">
                      {result.actions.map((a, i) => (
                        <li key={a} className="flex items-start gap-2 text-[#34536b] text-sm leading-relaxed">
                          <span className="text-[#b8893d] mt-1">→</span>
                          <span>{a}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* CTA */}
                <div className="bg-[#1B3A5C] text-white p-8 lg:p-10 rounded-sm relative overflow-hidden">
                  <div className="absolute inset-0 opacity-10 pointer-events-none">
                    <div className="absolute top-0 right-0 w-72 h-72 bg-[#b8893d] rounded-full blur-3xl"></div>
                  </div>
                  <div className="relative text-center max-w-xl mx-auto">
                    <h3 className="font-serif text-2xl md:text-3xl mb-3">
                      Persönliche Beratung anfragen
                    </h3>
                    <p className="text-white/80 mb-7 leading-relaxed">
                      Auf Basis Ihrer Antworten erstellen wir gerne eine individuelle Einschätzung Ihrer
                      Einbürgerungschancen. Klicken Sie unten, um uns Ihre Kontaktdaten zu übermitteln.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                      <Button
                        onClick={goToBeratung}
                        className="bg-[#b8893d] hover:bg-[#a07730] text-white px-8 py-6 tracking-[0.15em] text-xs"
                      >
                        BERATUNG ANFRAGEN
                        <ArrowRight className="ml-2 w-3.5 h-3.5" />
                      </Button>
                      <Button
                        onClick={restart}
                        variant="outline"
                        className="border-white/40 text-white hover:bg-white hover:text-[#1B3A5C] bg-transparent tracking-wider text-xs"
                      >
                        <RotateCcw className="mr-2 w-3.5 h-3.5" />
                        TEST NEU STARTEN
                      </Button>
                    </div>
                  </div>
                </div>

                <p className="text-[#34536b]/60 text-xs text-center max-w-2xl mx-auto px-4">
                  Dieser Selbsttest gibt eine erste Orientierung und ersetzt keine anwaltliche Beratung
                  im Einzelfall.
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
      <BackToTop />
    </div>
  );
};

export default EinbuergerungsSelbsttest;
